// pages/ReadyToDispatch.jsx
import { useState, useEffect, useMemo } from "react";
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { db } from "../services/firebase";
import { useSeller } from "../contexts/SellerContext";
import PageHeader from "../components/PageHeader";
import CourierSelectionList from "../components/CourierSelectionList";
import { jsPDF } from "jspdf";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import "./ReadyToDispatch.css";

// Payment Preference enum values & labels
const PAYMENT_LABELS = {
  PRE_PICKUP: "Pre-Pickup",
  WAREHOUSE: "At Warehouse",
  BUYER_PAYMENT: "After Buyer Pay",
};

const PAYMENT_ICONS = {
  PRE_PICKUP: "📦",
  WAREHOUSE: "🏭",
  BUYER_PAYMENT: "💳",
};

const formatPaymentPreference = (pref) => {
  if (!pref) return PAYMENT_LABELS.BUYER_PAYMENT;
  return PAYMENT_LABELS[pref] || pref;
};

// Resolve a single order's payment preference, tolerant of the various
// places it might be stored on the order document.
const getOrderPaymentPreference = (order) =>
  formatPaymentPreference(
    order.paymentPreference ||
      order.sellerPayment ||
      order.paymentMethod ||
      order.orderDetail?.paymentPreference,
  );

// Reverse-lookup a label back to its icon (falls back to the default icon)
const iconForPaymentLabel = (label) => {
  const entry = Object.entries(PAYMENT_LABELS).find(([, v]) => v === label);
  return entry ? PAYMENT_ICONS[entry[0]] : PAYMENT_ICONS.BUYER_PAYMENT;
};

// --- 📱 Cross-platform PDF save ---
// Strategy 1 (Android): Write directly to Documents folder — visible in the
//   Files app without any share sheet. Works on Android ≤12 with the
//   legacy storage flag. On Android 13+ falls back automatically.
// Strategy 2 (Android fallback): Write to Cache and open the Share sheet so
//   the user can save/open via any installed app.
// Web: standard browser download via pdf.save().
const savePdf = async (pdf, fileName) => {
  if (Capacitor.isNativePlatform()) {
    const base64 = pdf.output("datauristring").split(",")[1];

    // --- Strategy 1: Documents (public, persists in Files app) ---
    try {
      await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Documents,
        recursive: true,
      });
      alert(`✅ Bill saved!\nFind it in your Documents folder:\n${fileName}`);
      return;
    } catch (docErr) {
      console.warn(
        "Documents write failed, falling back to Share:",
        docErr.message,
      );
    }

    // --- Strategy 2: Cache + Share sheet ---
    await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    const { uri } = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Cache,
    });
    await Share.share({
      title: fileName,
      url: uri,
      dialogTitle: "Save bill to device",
    });
  } else {
    pdf.save(fileName);
  }
};

// --- 📊 Generate a CODE128 barcode as a PNG data-url using an offscreen canvas.
//     Works on Android WebView because it never touches the visible DOM.
const barcodeDataUrl = (value) =>
  new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, String(value), {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: true,
        margin: 6,
        background: "#ffffff",
        lineColor: "#000000",
      });
      resolve(canvas.toDataURL("image/png"));
    } catch (e) {
      console.warn("Barcode render error:", e);
      resolve(null);
    }
  });

// --- 📊 Module-level helpers (shared by bill + dispatch) ---
// Read a single size/variant entry tolerant of key-name differences
const getSizeData = (variant) => {
  const grossWT = parseFloat(variant.grossWT ?? variant.grossWt) || 0;
  const netwt = parseFloat(variant.netwt ?? variant.netWt) || 0;
  const qty = parseInt(variant.qty ?? variant.set ?? variant.quantity) || 0;
  const stampCertificateQtyPerSet = parseInt(variant.stampCertificateQtyPerSet);
  return { grossWT, netwt, qty, stampCertificateQtyPerSet };
};

// Totals derived from orderDetail: gross/net weights are per-piece, multiplied by qty.
const calculateOrderTotals = (order) => {
  let totalGross = 0;
  let totalSets = 0;
  let totalnetWt = 0;
  if (order.orderDetail && typeof order.orderDetail === "object") {
    Object.values(order.orderDetail).forEach((variant) => {
      const { grossWT, netwt, qty } = getSizeData(variant);
      totalGross += qty * grossWT;
      totalnetWt += qty * netwt;
      totalSets += qty;
    });
  }
  return { totalGross, totalSets, totalnetWt };
};

// Fine weight = net weight * (purity + wastage + specificationMC) / 100
const getOrderFineWeight = (order) => {
  const num = (v) => parseFloat(v) || 0;
  const addOnFactor =
    (num(order.purity) + num(order.wastage) + num(order.specificationMC)) / 100;
  let totalnetWt = 0;
  if (order.orderDetail && typeof order.orderDetail === "object") {
    Object.values(order.orderDetail).forEach((variant) => {
      const { netwt, qty } = getSizeData(variant);
      totalnetWt += qty * netwt;
    });
  }
  return totalnetWt * addOnFactor;
};

// AMOUNT = setMc + gramMcPerGram * (total net wt) + specMcPerGram * (total grossWt - netWt)
//          + stampCertificateCost * qty   (per piece; single piece uses qty = 1)
const getOrderAmounts = (order) => {
  const num = (v) => parseFloat(v) || 0;
  const setMc = num(order.setMc);
  const gramMcPerGram = num(order.netGramMc);
  const specMcPerGram = num(order.specificationGramRate);
  const stampCost = num(order.stampCertificateCost);

  let gramTotal = 0;
  let specTotal = 0;
  let stampTotal = 0;
  let totalSetMc = 0;
  if (order.orderDetail && typeof order.orderDetail === "object") {
    Object.values(order.orderDetail).forEach((variant) => {
      const { grossWT, netwt, qty, stampCertificateQtyPerSet } =
        getSizeData(variant);
      const sizeNet = qty * netwt;
      const sizeGross = qty * grossWT;
      totalSetMc += setMc * qty;
      gramTotal += gramMcPerGram * sizeNet;
      specTotal += specMcPerGram * (sizeGross - sizeNet);

      stampTotal += stampCost * stampCertificateQtyPerSet * qty;
    });
  }
  const totalAmount = totalSetMc + gramTotal + specTotal + stampTotal;
  return { totalAmount };
};

const getOrderTotalAmount = (order) => getOrderAmounts(order).totalAmount;

const ReadyToDispatch = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;

  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showSelection, setShowSelection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [operatorNames, setOperatorNames] = useState({});

  // --- 📊 Helper: Calculate summary for selected orders ---
  const calculateSummary = () => {
    const selectedItems = orders.filter((order) =>
      selectedOrders.includes(order.firestoreId),
    );

    let totalSets = 0;
    let totalWeight = 0;
    let totalnetWt = 0;
    let totalFineWt = 0;
    let totalAmount = 0;

    selectedItems.forEach((order) => {
      const {
        totalGross,
        totalSets: sets,
        totalnetWt: netWt,
      } = calculateOrderTotals(order);
      const fineWt = getOrderFineWeight(order);
      const { totalAmount: orderAmount } = getOrderAmounts(order);

      totalWeight += totalGross;
      totalSets += sets;
      totalnetWt += netWt;
      totalFineWt += fineWt;
      totalAmount += orderAmount;
    });

    return {
      sets: totalSets,
      weight: totalWeight.toFixed(3),
      totalnetWt: totalnetWt.toFixed(3),
      fineWt: totalFineWt.toFixed(3),
      amount: totalAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      totalAmountRaw: totalAmount,
    };
  };

  // --- 🔍 Helper: Find online sellerside JRE by operatorId ---
  const findSellersideJre = async (operatorId) => {
    try {
      const jreQuery = query(
        collection(db, "jreStatus"),
        where("role", "==", "sellerside"),
        where("operatorId", "==", operatorId),
        where("isOnline", "==", true),
      );
      const snapshot = await getDocs(jreQuery);
      if (snapshot.empty) return null;
      const docSnap = snapshot.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    } catch (err) {
      console.error("Error fetching JRE:", err);
      return null;
    }
  };

  // --- Handle courier selection from modal ---
  // Courier dispatch uses the SAME per-operator JRE rule as pickup:
  // each order is assigned its own operator's sellerside JRE.
  const handleCourierSelect = async (courier) => {
    console.log("🎯 Courier selected:", courier);
    dispatchOrdersViaCourier(courier);
  };

  // Resolve + validate the sellerside JRE for one operator (shared by pickup & courier)
  const resolveOperatorJre = async (operatorId, dispatchType, skippedErrors) => {
    const opName = operatorNames[operatorId] || operatorId;
    const jre = await findSellersideJre(operatorId);
    if (!jre || !jre.id) {
      skippedErrors.push(`No active JRE for operator ${opName}`);
      return null;
    }

    if (dispatchType === "courier" && jre.courior !== true) {
      skippedErrors.push(`Courier disabled for operator ${opName}`);
      return null;
    }
    return jre;
  };

  // --- 🚚 Dispatch via selected courier branch ---
  const dispatchOrdersViaCourier = async (courier) => {
    if (selectedOrders.length === 0) return;

    const selectedOrderObjects = orders.filter(
      (order) =>
        selectedOrders.includes(order.firestoreId) &&
        order.isBillPrinted === true,
    );

    if (selectedOrderObjects.length === 0) {
      alert("⚠️ Download the bill for the selected orders before dispatching.");
      return;
    }

    const summary = calculateSummary();

    const confirmMsg =
      `Dispatch ${selectedOrders.length} order(s) via ${courier.courierName}?\n\n` +
      `Branch: ${courier.branchCode}\n` +
      `Contact: ${courier.contactPerson} - ${courier.contactNumber}\n\n` +
      `Sets: ${summary.sets}  |  Gross: ${summary.weight}g  |  Net: ${summary.totalnetWt}g\n` +
      `Fine: ${summary.fineWt}g  |  Amount: ₹${summary.amount}`;

    if (!window.confirm(confirmMsg)) return;

    setDispatching(true);

    try {
      const batch = writeBatch(db);
      const dispatchedIds = [];
      const skippedErrors = [];
      const now = new Date();

      // Group by operator first so every order gets ITS OWN operator's JRE
      const byOperator = {};
      selectedOrderObjects.forEach((order) => {
        const operatorId = order.operatorId || "UNKNOWN";
        if (!byOperator[operatorId]) byOperator[operatorId] = [];
        byOperator[operatorId].push(order);
      });

      if (Object.keys(byOperator).includes("UNKNOWN")) {
        alert("❌ Some selected orders are missing an operator ID.");
        return;
      }

      for (const [operatorId, opOrders] of Object.entries(byOperator)) {
        const jre = await resolveOperatorJre(operatorId, "courier", skippedErrors);
        if (!jre) continue;

        opOrders.forEach((order) => {
          batch.update(doc(db, "orderList", order.firestoreId), {
            orderStatus: "Assigned",
            courierId: courier.branchId,
            courierName: courier.courierName || null,
            courierBranchName: courier.branchName || null,
            courierBranchCode: courier.branchCode || null,
            courierContactPerson: courier.contactPerson || null,
            courierContactNumber: courier.contactNumber || null,
            courierAlternateContactNumber: courier.alternateContactNumber || null,
            jreId: jre.id,
            assignedAt: now,
            dispatchedAt: now,
            dispatchType: "courier",
          });
          dispatchedIds.push(order.firestoreId);
        });
      }

      if (dispatchedIds.length === 0) {
        alert(
          "❌ No orders could be dispatched:\n\n" + skippedErrors.join("\n"),
        );
        return;
      }

      await batch.commit();

      const remainingOrders = orders.filter(
        (order) => !dispatchedIds.includes(order.firestoreId),
      );
      setOrders(remainingOrders);
      setSelectedOrders((prev) =>
        prev.filter((id) => !dispatchedIds.includes(id)),
      );

      let msg = `✅ Dispatched ${dispatchedIds.length} order(s) via ${courier.courierName} to their respective operator JREs!`;
      if (skippedErrors.length > 0) {
        msg += "\n\nSkipped:\n" + skippedErrors.join("\n");
      }
      alert(msg);
    } catch (err) {
      console.error("Courier dispatch error:", err);
      alert(`❌ Dispatch failed: ${err.message}`);
    } finally {
      setDispatching(false);
    }
  };

  // --- 🚚 Main dispatch function (multi-operator aware) ---
  const dispatchOrders = async (dispatchType) => {
    if (selectedOrders.length === 0) return;

    const selectedOrderObjects = orders.filter(
      (order) =>
        selectedOrders.includes(order.firestoreId) &&
        order.isBillPrinted === true,
    );

    if (selectedOrderObjects.length === 0) {
      alert("⚠️ Download the bill for the selected orders before dispatching.");
      return;
    }

    const byOperator = {};
    selectedOrderObjects.forEach((order) => {
      const operatorId = order.operatorId || "UNKNOWN";
      if (!byOperator[operatorId]) byOperator[operatorId] = [];
      byOperator[operatorId].push(order);
    });

    if (Object.keys(byOperator).includes("UNKNOWN")) {
      alert("❌ Some selected orders are missing an operator ID.");
      return;
    }

    const summary = calculateSummary();

    const confirmMsg =
      `Dispatch ${
        selectedOrders.length
      } order(s) via ${dispatchType.toUpperCase()}?\n\n` +
      `Operators: ${Object.keys(byOperator).length}\n` +
      `Sets: ${summary.sets}  |  Gross: ${summary.weight}g  |  Net: ${summary.totalnetWt}g\n` +
      `Fine: ${summary.fineWt}g  |  Amount: ₹${summary.amount}\n` +
      `Total: ₹${summary.totalAmount}`;
    if (!window.confirm(confirmMsg)) return;

    setDispatching(true);

    try {
      const batch = writeBatch(db);
      const dispatchedIds = [];
      const skippedErrors = [];

      for (const [operatorId, opOrders] of Object.entries(byOperator)) {
        const opName = operatorNames[operatorId] || operatorId;
        const jre = await findSellersideJre(operatorId);
        if (!jre || !jre.id) {
          skippedErrors.push(`No active JRE for operator ${opName}`);
          continue;
        }

        if (dispatchType === "courier" && jre.courior !== true) {
          skippedErrors.push(`Courier disabled for operator ${opName}`);
          continue;
        }

        let totalOrderValue = 0;
        if (dispatchType === "pickup") {
          // Total for THIS operator = Σ (fineWeight * segmentRate + amount)
          // Different operators are evaluated independently (per-operator maxValue).
          totalOrderValue = opOrders.reduce((sum, order) => {
            const fineWt =
              parseFloat(getOrderFineWeight(order).toFixed(3)) || 0;
            const amount = getOrderTotalAmount(order);

            // Get rate based on segment or metal from the order
            const segment = order.segment || order.metal || "DEFAULT";
            const segmentRate = parseFloat(
              jre.rates?.[segment] || jre.stateRate || 0,
            );

            return sum + fineWt * segmentRate + amount;
          }, 0);
          const maxValue = jre.maxValue ?? 0;
          if (totalOrderValue > maxValue) {
            skippedErrors.push(
              `Pickup limit exceeded for operator ${opName} ` +
                `(₹${totalOrderValue.toFixed(2)} > max ₹${maxValue.toFixed(
                  2,
                )})`,
            );
            continue;
          }
        }

        opOrders.forEach((order) => {
          batch.update(doc(db, "orderList", order.firestoreId), {
            orderStatus: "Assigned",
            jreId: jre.id,
            assignedAt: new Date(),
            dispatchedAt: new Date(),
            dispatchType,
          });
          dispatchedIds.push(order.firestoreId);
        });

        if (dispatchType === "pickup") {
          batch.update(doc(db, "jreStatus", jre.id), {
            currentValue: Math.max(0, (jre.maxValue ?? 0) - totalOrderValue),
          });
        }
      }

      if (dispatchedIds.length === 0) {
        alert(
          "❌ No orders could be dispatched:\n\n" + skippedErrors.join("\n"),
        );
        return;
      }

      await batch.commit();

      const remainingOrders = orders.filter(
        (order) => !dispatchedIds.includes(order.firestoreId),
      );
      setOrders(remainingOrders);
      setSelectedOrders((prev) =>
        prev.filter((id) => !dispatchedIds.includes(id)),
      );

      let msg =
        `✅ Dispatched ${dispatchedIds.length} order(s) via ${dispatchType} ` +
        `to their respective operator JREs!`;
      if (skippedErrors.length > 0) {
        msg += "\n\nSkipped:\n" + skippedErrors.join("\n");
      }
      alert(msg);
    } catch (err) {
      console.error("Dispatch error:", err);
      alert(`❌ Dispatch failed: ${err.message}`);
    } finally {
      setDispatching(false);
    }
  };

  // --- 📥 Fetch all RTD orders once ---
  useEffect(() => {
    if (!sellerId) return;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, "orderList"),
          where("sellerId", "==", sellerId),
          where("orderStatus", "==", "RTD"),
        );
        const snapshot = await getDocs(q);

        const fetchedOrders = [];
        snapshot.forEach((docSnap) => {
          fetchedOrders.push({
            firestoreId: docSnap.id,
            ...docSnap.data(),
          });
        });
        console.log(fetchedOrders);
        setOrders(fetchedOrders);
      } catch (err) {
        console.error("Fetch orders error:", err);
        setError("Failed to load ready-to-dispatch orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [sellerId]);

  // --- 🗂️ Group orders by operatorId AND payment preference ---
  // Each distinct (operatorId, paymentPreference) pair becomes its own group/card,
  // so an operator with mixed payment modes shows up as separate sections.
  // The operator NAME is fetched for display; the raw ID is never shown in UI/PDF.
  const groupedByOperator = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      const operatorId = order.operatorId || "UNKNOWN";
      const operatorName =
        order.operatorName ||
        order.operatorOrganizationName ||
        operatorNames[operatorId] ||
        operatorId;
      const paymentPreference = getOrderPaymentPreference(order);
      const key = `${operatorId}::${paymentPreference}`;
      if (!map[key]) {
        map[key] = { operatorId, operatorName, paymentPreference, items: [] };
      }
      map[key].items.push(order);
    });
    return Object.values(map).sort((a, b) => {
      const opCompare = (a.operatorName || a.operatorId).localeCompare(b.operatorName || b.operatorId);
      if (opCompare !== 0) return opCompare;
      return a.paymentPreference.localeCompare(b.paymentPreference);
    });
  }, [orders, operatorNames]);

  // Resolve operator display names (IDs -> organization names)
  useEffect(() => {
    const ids = [...new Set(orders.map((o) => o.operatorId).filter((id) => id && id !== "UNKNOWN"))];
    if (ids.length === 0) return;
    const missing = ids.filter((id) => !operatorNames[id]);
    if (missing.length === 0) return;
    const fetchNames = async () => {
      const names = {};
      await Promise.all(
        missing.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "operatorregistrations", id));
            if (snap.exists()) {
              const d = snap.data();
              names[id] = d.organizationName || d.businessName || d.operatorName || d.name || id;
            } else {
              names[id] = id;
            }
          } catch {
            names[id] = id;
          }
        }),
      );
      setOperatorNames((prev) => ({ ...prev, ...names }));
    };
    fetchNames();
  }, [orders, operatorNames]);

  // --- 🔘 Toggle a single order (selectable once buyer confirmed) ---
  const toggleOrderSelection = (order) => {
    if (order.buyerConfirmation !== true) {
      alert("⚠️ Buyer has not confirmed this order yet. Cannot select.");
      return;
    }
    const id = order.firestoreId;
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // --- 🔘 Toggle ALL buyer-confirmed orders of a group (card tick) ---
  const toggleOperatorSelection = (group) => {
    const confirmedIds = group.items
      .filter((o) => o.buyerConfirmation === true)
      .map((o) => o.firestoreId);
    const selectedConfirmed = confirmedIds.filter((id) =>
      selectedOrders.includes(id),
    );
    const allSelected =
      confirmedIds.length > 0 &&
      selectedConfirmed.length === confirmedIds.length;

    setSelectedOrders((prev) => {
      if (allSelected) {
        return prev.filter((id) => !confirmedIds.includes(id));
      }
      return [...new Set([...prev, ...confirmedIds])];
    });
  };

  // --- ✅ Mark the given orders as bill printed (isBillPrinted: true) in Firebase ---
  const markBillsPrinted = async (ordersToMark) => {
    try {
      const batch = writeBatch(db);
      const now = new Date();
      ordersToMark.forEach((order) => {
        batch.update(doc(db, "orderList", order.firestoreId), {
          isBillPrinted: true,
          billPrintedAt: now,
        });
      });
      await batch.commit();
      setOrders((prev) =>
        prev.map((o) =>
          ordersToMark.some((m) => m.firestoreId === o.firestoreId)
            ? { ...o, isBillPrinted: true, billPrintedAt: now }
            : o,
        ),
      );
      return true;
    } catch (err) {
      console.error("Error marking bills printed:", err);
      alert(`❌ Failed to mark bill as printed: ${err.message}`);
      return false;
    }
  };

  // --- 📥 Build the PDF with jsPDF — layout mirrors the old OperatorBill HTML component.
  //     Section 1 : DISPATCH BILL title → meta + QR → bordered table → divider → meta + QR (copy)
  //     Section 2 : Item-wise Details — per-order block with QR + barcode lines
  //     Footer    : "End of operator bill"
  //
  //  NOTE: operatorId is still the unit the bill is generated for; when the
  //  operator has multiple payment-preference groups, pass the specific
  //  group's items via `groupItems` so the bill only covers that group.
  const downloadOperatorBill = async (operatorId, groupItems, operatorName) => {
    const displayOperatorName = operatorName || operatorNames[operatorId] || operatorId;
    const eligibleIds = new Set(groupItems.map((o) => o.firestoreId));
    const selectedOrderObjects = orders.filter(
      (o) =>
        o.operatorId === operatorId &&
        eligibleIds.has(o.firestoreId) &&
        selectedOrders.includes(o.firestoreId) &&
        o.buyerConfirmation === true,
    );
    if (selectedOrderObjects.length === 0) {
      alert("No confirmed & selected orders to download for this operator.");
      return;
    }

    const ok = await markBillsPrinted(selectedOrderObjects);
    if (!ok) return;

    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const PW = pdf.internal.pageSize.getWidth(); // 595.28
      const PH = pdf.internal.pageSize.getHeight(); // 841.89
      const ML = 36; // left margin
      const MR = PW - 36; // right margin

      const now = new Date();
      const isoTime = now.toISOString();
      const readableTime = now.toLocaleString();

      // ── Page helpers ──────────────────────────────────────────────────────
      let y = ML;
      const newPage = () => {
        pdf.addPage();
        y = ML;
      };
      const need = (h) => {
        if (y + h > PH - 30) newPage();
      };

      // Dashed horizontal rule (simulate with short segments)
      const dashedRule = () => {
        need(8);
        pdf.setDrawColor(0);
        pdf.setLineWidth(0.8);
        const dash = 4,
          gap = 3;
        let x = ML;
        while (x < MR) {
          pdf.line(x, y, Math.min(x + dash, MR), y);
          x += dash + gap;
        }
        y += 8;
      };

      // Draw a full bordered table row (cells defined as [{text, w, bold?}])
      const tableRow = (cells, rowH, bgHex) => {
        need(rowH + 2);
        let x = ML;
        cells.forEach(({ text, w, bold, align }) => {
          if (bgHex) {
            const rgb = [
              parseInt(bgHex.slice(1, 3), 16),
              parseInt(bgHex.slice(3, 5), 16),
              parseInt(bgHex.slice(5, 7), 16),
            ];
            pdf.setFillColor(...rgb);
            pdf.rect(x, y, w, rowH, "F");
          }
          pdf.setDrawColor(0);
          pdf.setLineWidth(0.3);
          pdf.rect(x, y, w, rowH, "S");
          pdf.setFont("helvetica", bold ? "bold" : "normal");
          pdf.setFontSize(8);
          const tx = align === "right" ? x + w - 3 : x + 3;
          pdf.text(String(text ?? ""), tx, y + rowH - 3, {
            align: align || "left",
          });
          x += w;
        });
        y += rowH;
      };

      // Pre-compute all order data
      const computed = selectedOrderObjects.map((order) => {
        const { totalGross, totalSets, totalnetWt } =
          calculateOrderTotals(order);
        const amount = getOrderTotalAmount(order);
        return { order, totalGross, totalSets, totalnetWt, amount };
      });
      const totals = computed.reduce(
        (acc, c) => {
          acc.sets += c.totalSets;
          acc.gross += c.totalGross;
          acc.net += c.totalnetWt;
          acc.amount += c.amount;
          return acc;
        },
        { sets: 0, gross: 0, net: 0, amount: 0 },
      );

      // Build summary QR payload (same structure as the old HTML bill)
      const qrPayload = JSON.stringify({
        operator: displayOperatorName,
        operatorId,
        generatedAt: isoTime,
        totals: {
          sets: totals.sets,
          gross: Number(totals.gross.toFixed(3)),
          net: Number(totals.net.toFixed(3)),
          amount: Number(totals.amount.toFixed(2)),
        },
        orders: computed.map((c) => ({
          id: c.order.firestoreId,
          barcode: c.order.barcode || c.order.firestoreId,
          product: c.order.productName,
          category: c.order.category,
          set: c.totalSets,
          gross: Number(c.totalGross.toFixed(3)),
          net: Number(c.totalnetWt.toFixed(3)),
          amount: Number(c.amount.toFixed(2)),
        })),
      });

      // Pre-render summary QR as data-url
      let summaryQrUrl = null;
      try {
        summaryQrUrl = await QRCode.toDataURL(qrPayload, {
          width: 100,
          margin: 1,
        });
      } catch (_) {}

      // ══ Helper: draw the meta block (operator info left, QR+barcode centered) ═
      const drawMeta = async () => {
        const QR_SIZE = 90;
        const BC_H = 28;
        const BC_W = 180;
        const metaStartY = y;

        // Left column — operator info
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text("Operator:", ML, y + 12);
        pdf.setFont("helvetica", "normal");
        pdf.text(displayOperatorName, ML + 48, y + 12);
        pdf.setFont("helvetica", "bold");
        pdf.text("Generated:", ML, y + 24);
        pdf.setFont("helvetica", "normal");
        pdf.text(readableTime, ML + 52, y + 24);
        pdf.setFont("helvetica", "bold");
        pdf.text("Total Orders:", ML, y + 36);
        pdf.setFont("helvetica", "normal");
        pdf.text(String(selectedOrderObjects.length), ML + 62, y + 36);

        // Centre — QR image centred on page width
        const qrX = (PW - QR_SIZE) / 2;
        if (summaryQrUrl) {
          pdf.addImage(summaryQrUrl, "PNG", qrX, metaStartY, QR_SIZE, QR_SIZE);
        }
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.text("Scan for all details", PW / 2, metaStartY + QR_SIZE + 9, {
          align: "center",
        });

        // Barcode centred below QR.
        // operatorId already contains the full value (e.g. "OP-BCF5A4B8") — do NOT add OP- prefix again.
        const opBcUrl = await barcodeDataUrl(operatorId);
        const bcX = (PW - BC_W) / 2;
        if (opBcUrl) {
          // displayValue:true inside barcodeDataUrl already draws the text inside the image — no extra text label needed
          pdf.addImage(
            opBcUrl,
            "PNG",
            bcX,
            metaStartY + QR_SIZE + 14,
            BC_W,
            BC_H,
          );
        }

        y = metaStartY + QR_SIZE + 14 + BC_H + 10;
      };

      // ══════════════════════════════════════════════════════════════════════
      // SECTION 1 — DISPATCH BILL
      // ══════════════════════════════════════════════════════════════════════

      // Title
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("DISPATCH BILL", PW / 2, y + 16, { align: "center" });
      y += 20;
      dashedRule();

      // Meta block
      await drawMeta();

      // Table — columns sum exactly to CW = MR-ML = (595-36-36) = 523pt
      // Item:130 + Cat:85 + Set:38 + Gross:72 + Net:72 + Amt:126 = 523
      // Amount column is wide enough for "Rs.12345.00" at font-size 8
      const COL = { item: 130, cat: 85, set: 38, gross: 72, net: 72, amt: 126 };
      // Header row
      tableRow(
        [
          { text: "Item", w: COL.item, bold: true },
          { text: "Category", w: COL.cat, bold: true },
          { text: "Set", w: COL.set, bold: true, align: "right" },
          { text: "Gross (g)", w: COL.gross, bold: true, align: "right" },
          { text: "Net (g)", w: COL.net, bold: true, align: "right" },
          { text: "Amount", w: COL.amt, bold: true, align: "right" },
        ],
        14,
        "#f0f0f0",
      );

      // Data rows — use "Rs." prefix (Helvetica has no rupee glyph)
      for (const {
        order,
        totalGross,
        totalSets,
        totalnetWt,
        amount,
      } of computed) {
        tableRow(
          [
            { text: (order.productName || "").substring(0, 20), w: COL.item },
            { text: (order.category || "").substring(0, 13), w: COL.cat },
            { text: String(totalSets), w: COL.set, align: "right" },
            { text: totalGross.toFixed(3), w: COL.gross, align: "right" },
            { text: totalnetWt.toFixed(3), w: COL.net, align: "right" },
            { text: `Rs.${amount.toFixed(2)}`, w: COL.amt, align: "right" },
          ],
          13,
        );
      }

      // Totals row — Item+Category cells merged for "TOTAL" label
      tableRow(
        [
          { text: "TOTAL", w: COL.item + COL.cat, bold: true },
          { text: String(totals.sets), w: COL.set, bold: true, align: "right" },
          {
            text: totals.gross.toFixed(3),
            w: COL.gross,
            bold: true,
            align: "right",
          },
          {
            text: totals.net.toFixed(3),
            w: COL.net,
            bold: true,
            align: "right",
          },
          {
            text: `Rs.${totals.amount.toFixed(2)}`,
            w: COL.amt,
            bold: true,
            align: "right",
          },
        ],
        14,
        "#f7f7f7",
      );

      y += 4;
      dashedRule();

      // Second copy of meta block (receipt duplicate)
      await drawMeta();

      // ══════════════════════════════════════════════════════════════════════
      // SECTION 2 — Item-wise Details
      // ══════════════════════════════════════════════════════════════════════
      dashedRule();
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      need(16);
      pdf.text("Item-wise Details", ML, y + 11);
      y += 14;
      dashedRule();

      for (const {
        order,
        totalGross,
        totalSets,
        totalnetWt,
        amount,
      } of computed) {
        const barcodeVal = order.barcode || order.firestoreId;

        // Per-order QR
        let oQrUrl = null;
        try {
          const oQr = JSON.stringify({
            operator: operatorId,
            orderId: order.firestoreId,
            barcode: barcodeVal,
            product: order.productName,
            category: order.category,
            set: totalSets,
            gross: Number(totalGross.toFixed(3)),
            net: Number(totalnetWt.toFixed(3)),
            amount: Number(amount.toFixed(2)),
            generatedAt: isoTime,
          });
          oQrUrl = await QRCode.toDataURL(oQr, { width: 80, margin: 1 });
        } catch (_) {}

        const ITEM_QR = 80;
        const ITEM_BC_W = 180;
        const ITEM_BC_H = 28;
        const itemStart = y;
        need(ITEM_QR + ITEM_BC_H + 30);

        // Left: item details
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text(order.productName || "", ML, y + 11);
        y += 13;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(order.category || "", ML, y + 9);
        y += 11;
        pdf.text(`Set ${totalSets}`, ML, y + 9);
        y += 11;
        pdf.text(`Gross ${totalGross.toFixed(3)}`, ML, y + 9);
        y += 11;
        pdf.text(`Net ${totalnetWt.toFixed(3)}`, ML, y + 9);
        y += 11;
        pdf.text(`MC: Rs.${amount.toFixed(2)}`, ML, y + 9);
        y += 11;

        // QR centred on page
        const itemQrX = (PW - ITEM_QR) / 2;
        if (oQrUrl) {
          pdf.addImage(oQrUrl, "PNG", itemQrX, itemStart, ITEM_QR, ITEM_QR);
        }

        // Barcode centred below QR — displayValue:true already shows value inside image
        const itemBcX = (PW - ITEM_BC_W) / 2;
        const itemBcUrl = await barcodeDataUrl(barcodeVal);
        if (itemBcUrl) {
          pdf.addImage(
            itemBcUrl,
            "PNG",
            itemBcX,
            itemStart + ITEM_QR + 6,
            ITEM_BC_W,
            ITEM_BC_H,
          );
        }

        // Ensure y clears both left text and centred QR+barcode
        y = Math.max(y, itemStart + ITEM_QR + ITEM_BC_H + 14);
        dashedRule();
      }

      // ── Footer ────────────────────────────────────────────────────────────
      need(24);
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(8);
      pdf.text("— End of operator bill —", PW / 2, y + 10, { align: "center" });
      y += 14;
      pdf.text(readableTime, PW / 2, y + 10, { align: "center" });

      // ── Save ──────────────────────────────────────────────────────────────
      await savePdf(pdf, `dispatch-bill-${displayOperatorName}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert(`❌ Failed to generate PDF: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Ready to Dispatch" />
        <div className="dispatch-container">Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Ready to Dispatch" />
        <div className="dispatch-container">
          <p className="error">{error}</p>
        </div>
      </>
    );
  }

  const summary = calculateSummary();

  // Orders that are selected AND have their bill downloaded (unlock dispatch)
  const selectedPrintedCount = orders.filter(
    (o) => selectedOrders.includes(o.firestoreId) && o.isBillPrinted === true,
  ).length;
  const canDispatch = selectedPrintedCount > 0 && !dispatching;

  return (
    <>
      <PageHeader title="Ready to Dispatch" />
      <div className="dispatch-container">
        <p className="results-count">
          {orders.length} request{orders.length !== 1 ? "s" : ""} found across{" "}
          {groupedByOperator.length} group
          {groupedByOperator.length !== 1 ? "s" : ""}
        </p>

        <div className="operator-groups">
          {orders.length === 0 ? (
            <p className="no-results">No orders ready to dispatch.</p>
          ) : (
            groupedByOperator.map((group) => {
              const confirmedIds = group.items
                .filter((o) => o.buyerConfirmation === true)
                .map((o) => o.firestoreId);
              const selectedConfirmed = confirmedIds.filter((id) =>
                selectedOrders.includes(id),
              );
              const allSelected =
                confirmedIds.length > 0 &&
                selectedConfirmed.length === confirmedIds.length;
              const someSelected = selectedConfirmed.length > 0 && !allSelected;
              const hasSelection = selectedConfirmed.length > 0;
              const groupKey = `${group.operatorId}::${group.paymentPreference}`;

              return (
                <div className="operator-group" key={groupKey}>
                  <div
                    className="operator-header"
                    onClick={() => toggleOperatorSelection(group)}
                  >
                    <div
                      className={`operator-select ${
                        allSelected ? "selected" : ""
                      }`}
                    >
                      {allSelected ? (
                        <span className="tick-icon">✔</span>
                      ) : someSelected ? (
                        <span className="tick-icon dash">–</span>
                      ) : (
                        <span className="empty-box" />
                      )}
                    </div>
                    <div className="operator-info">
                      <h2>
                        {group.operatorId === "UNKNOWN"
                          ? "Unknown Operator"
                          : `Operator: ${group.operatorName || group.operatorId}`}
                        {" "}
                        
                      </h2>
                      <span>
                        {group.items.length} order
                        {group.items.length !== 1 ? "s" : ""}
                        {selectedConfirmed.length > 0
                          ? ` • ${selectedConfirmed.length} selected`
                          : ""}
                        <p>
                          {confirmedIds.length} confirmed
                          {" • "}
                          {group.items.length - confirmedIds.length} pending
                        </p>
                        <span className="payment-pref-badge">
                          {iconForPaymentLabel(group.paymentPreference)}{" "}
                          {group.paymentPreference}
                        </span>
                      </span>
                    </div>
                    <button
                      className={`operator-print-switch no-print ${
                        hasSelection ? "active" : ""
                      }`}
                      disabled={!hasSelection}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadOperatorBill(group.operatorId, group.items, group.operatorName);
                      }}
                      title="Download bill for this group"
                      aria-label="Download bill for this group"
                    >
                      📥 Download
                    </button>
                  </div>

                  <div className="order-slider">
                    {group.items.map((order) => {
                      const { totalGross, totalSets, totalnetWt } =
                        calculateOrderTotals(order);
                      const isSelected = selectedOrders.includes(
                        order.firestoreId,
                      );
                      const isConfirmed = order.buyerConfirmation === true;
                      const isBillPrinted = order.isBillPrinted === true;
                      const imageUrl = order.images?.[0]?.url?.trim();

                      return (
                        <div
                          key={order.firestoreId}
                          className={
                            `order-card ${isSelected ? "selected" : ""} ` +
                            `${isConfirmed ? "" : "not-confirmed"} ` +
                            `${isBillPrinted ? "" : "bill-not-printed"}`
                          }
                          onClick={() => toggleOrderSelection(order)}
                        >
                          <div className="selection-checkbox">
                            {isSelected ? (
                              <span className="tick-icon">✔</span>
                            ) : (
                              <span className="empty-box" />
                            )}
                          </div>

                          {!isConfirmed && (
                            <div className="confirm-badge">Pending</div>
                          )}

                          {isConfirmed && !isBillPrinted && (
                            <div className="bill-badge">
                              Bill Not Downloaded
                            </div>
                          )}

                          <div className="order-image">
                            {imageUrl ? (
                              <img src={imageUrl} alt={order.productName} />
                            ) : (
                              <div className="image-placeholder">No Image</div>
                            )}
                          </div>
                          <h3>{order.productName}</h3>
                          <p>
                            {order.category} • {totalGross.toFixed(3)}g • Net{" "}
                            {totalnetWt.toFixed(3)}g • Set {totalSets}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="selection-section">
          <div className="selection-header">
            <span>Selected: {selectedOrders.length}</span>
            <button
              className="toggle-selection"
              onClick={() => setShowSelection(!showSelection)}
              aria-label={
                showSelection
                  ? "Hide selection details"
                  : "Show selection details"
              }
            >
              {showSelection ? "▼" : "▲"}
            </button>
          </div>

          {showSelection && selectedOrders.length > 0 && (
            <div className="selection-summary">
              <h2>Selection Summary ({selectedOrders.length} items)</h2>
              <div className="summary-section">
                <div className="summary-table">
                  <div className="summary-row">
                    <span>SETS</span>
                    <span>{summary.sets}</span>
                  </div>
                  <div className="summary-row">
                    <span>GR. WEIGHT</span>
                    <span>{summary.weight}</span>
                  </div>
                  <div className="summary-row">
                    <span>NET. WEIGHT</span>
                    <span>{summary.totalnetWt}</span>
                  </div>
                  <div className="summary-row">
                    <span>FINE WEIGHT</span>
                    <span>{summary.fineWt}</span>
                  </div>
                  <div className="summary-row">
                    <span>AMOUNT</span>
                    <span>₹{summary.amount}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rtd-action-buttons">
            <button
              className="courier-btn"
              onClick={() => setShowCourierModal(true)}
              disabled={!canDispatch}
              title={
                canDispatch
                  ? "Send selected orders by courier"
                  : "Download the bill to enable dispatch"
              }
            >
              {dispatching ? "Processing..." : "Send By Courier"}
            </button>
            <button
              className="dispatch-btn"
              onClick={() => dispatchOrders("pickup")}
              disabled={!canDispatch}
              title={
                canDispatch
                  ? "Request pickup for selected orders"
                  : "Download the bill to enable dispatch"
              }
            >
              {dispatching ? "Processing..." : "Request To Pickup"}
            </button>
          </div>
          {!canDispatch && selectedOrders.length > 0 && (
            <p className="dispatch-locked-note">
              🔒 Download the bill to unlock dispatch for selected orders.
            </p>
          )}
        </div>
      </div>

      {/* Offscreen bill wrapper removed — PDF is now built directly with jsPDF */}

      {/* Courier Selection Modal */}
      <CourierSelectionList
        districtCode={seller?.district}
        isOpen={showCourierModal}
        onClose={() => setShowCourierModal(false)}
        onSelectCourier={handleCourierSelect}
      />
    </>
  );
};

export default ReadyToDispatch;